import { Link } from 'react-router-dom';
import { characterAvatarUrl, raceImageUrl } from '@/lib/assetUrl';
import { useLocale } from '@/context/LocaleContext';
import { LanguageSelector } from '@/components/LanguageSelector';
import { useServerTime } from '@/hooks/useServerTime';
import { useUnreadMessagesCount } from '@/hooks/useMessages';
import { useUnreadNotificationCount } from '@/hooks/useNotifications';
import type { PlayerData } from '@/vite-env.d';

interface PlayerSidebarProps {
  player: PlayerData | null;
  charId?: number;
  isLoading?: boolean;
}

export function PlayerSidebar({ player, charId: charIdProp, isLoading }: PlayerSidebarProps) {
  const { t } = useLocale();
  const { data: serverTime } = useServerTime();
  const { data: unreadCount = 0 } = useUnreadMessagesCount(charIdProp);
  const { data: unreadNotifications = 0 } = useUnreadNotificationCount(charIdProp);

  const refObjId = player?.RefObjID ?? 14875;
  const showAvatar = Boolean(player?.RefObjID);

  return (
    <div className="sidebar">
      <div className="avatar">
        {showAvatar ? (
          <img
            id="character-avatar"
            src={characterAvatarUrl(refObjId)}
            alt="Character Avatar"
            style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              background: 'rgba(255,255,255,0.1)',
              borderRadius: '50%',
              animation: 'pulse 1.5s infinite',
            }}
          />
        )}
      </div>

      {isLoading && (
        <div id="player-loading-msg" style={{ textAlign: 'center', fontSize: '0.85em', color: '#e6c98a', marginBottom: 10 }}>
          <span style={{ animation: 'pulse 1.5s infinite' }}>⏳ {t('loading_data')}</span>
        </div>
      )}

      <div className="info">
        <span id="player-race">
          <img src={raceImageUrl(Number(refObjId))} alt="Race" className="race-icon" />
        </span>
        <span>Lv:</span>
        <span className="value" id="player-lv">
          {player?.CurLevel ?? ''}
        </span>
        <span>|</span>
        <span className="value" id="charname-placeholder">
          {player?.CharName16 ?? ''}
        </span>
      </div>

      <div className="info-separator" />

      <div className="server-time-container" id="server-time-container" style={{ display: serverTime ? 'block' : 'none' }}>
        <div className="server-time-label">{t('server_time')}</div>
        <div className="server-time-display" id="server-time-display">
          {serverTime?.formatted_time ?? '--:--:--'}
        </div>
      </div>

      <div className="quick-actions">
        <div className="quick-actions-row">
          <a href="/guide" className="quick-action-btn" title={t('guide')}>
            <QuickIcon d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
          </a>
          <Link to="/jobsystem" className="quick-action-btn" title={t('job_system')}>
            <QuickIcon d="M2 7h20v14H2z M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
          </Link>
          <Link to="/vipsystem" className="quick-action-btn" title={t('vip_system')}>
            <QuickIcon d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </Link>
          <Link to="/cupons" className="quick-action-btn" title={t('cupon_title')}>
            <QuickIcon d="M21 8.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8.5M1 3h22v5.5H1z M10 12h4" />
          </Link>
          <Link to="/setting" className="quick-action-btn" title={t('settings')}>
            <QuickIcon
              circle={{ cx: 12, cy: 12, r: 3 }}
              d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"
            />
          </Link>
        </div>
        <div className="quick-actions-row">
          <Link to="/messages" className="quick-action-btn" title={t('messages')}>
            {unreadCount > 0 ? (
              <span className="badge-unread" id="messages-badge" style={{ display: 'flex' }}>
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            ) : (
              <span className="badge-unread" id="messages-badge" style={{ display: 'none' }}>
                0
              </span>
            )}
            <QuickIcon d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z M22 6 12 13 2 6" />
          </Link>
          <Link to="/notifications" className="quick-action-btn" title={t('notifications_title')}>
            {unreadNotifications > 0 ? (
              <span className="badge-unread" id="notifications-badge" style={{ display: 'flex' }}>
                {unreadNotifications > 99 ? '99+' : unreadNotifications}
              </span>
            ) : (
              <span className="badge-unread" id="notifications-badge" style={{ display: 'none' }}>
                0
              </span>
            )}
            <QuickIcon d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9 M13.73 21a2 2 0 0 1-3.46 0" />
          </Link>
          <Link to="/donation" className="quick-action-btn" title={t('donation')}>
            <QuickIcon d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </Link>
          <Link to="/dailytasks" className="quick-action-btn" title={t('dt_title')}>
            <QuickIcon d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2m-6 9 2 2 4-4" />
          </Link>
          <Link to="/surveys" className="quick-action-btn" title={t('surveys')}>
            <QuickIcon d="M9 17H7A5 5 0 0 1 7 7h2 M15 7h2a5 5 0 1 1 0 10h-2 M8 12h8" />
          </Link>
        </div>
      </div>

      <div className="language-section">
        <div className="language-title-container">
          <div className="language-title-line" />
          <h3 className="language-title">{t('languages')}</h3>
          <div className="language-title-line" />
        </div>
        <LanguageSelector />
      </div>

      <div className="sidebar-version-label">
        Beta v1.0.5
        {typeof window !== 'undefined' && window.__APPWEB_LAST_AUTH_AT__
          ? ` · auth ${new Date(window.__APPWEB_LAST_AUTH_AT__).toLocaleTimeString()}`
          : ' · auth —'}
      </div>
    </div>
  );
}

function QuickIcon({
  d,
  circle,
}: {
  d: string;
  circle?: { cx: number; cy: number; r: number };
}) {
  return (
    <svg width="20" height="20" fill="none" stroke="#e6c98a" strokeWidth="2" viewBox="0 0 24 24">
      {circle ? <circle cx={circle.cx} cy={circle.cy} r={circle.r} /> : null}
      <path d={d} />
    </svg>
  );
}
